import dot from 'dot-prop-immutable'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'

import { fetchSingleuseLink, setShareType } from 'apps/templates/actions/currentTemplateActions'
import { createTemplate, updateTemplate } from 'apps/templates/actions/templatesActions'
import * as atypes from 'apps/templates/constants/ReduxActionTypes'
import { TemplateDraftInvalidPropertyError } from 'apps/templates/errors'
import {
  getDependentBotFields,
  getHasIconDependency,
  getIsIconItem,
} from 'apps/templates/models/ContentMap/utils'
import { getIsAvailable, getIsProtected } from 'apps/templates/selectors/installedTemplateSelectors'
import { FSOAdapter } from 'common/cms/models/FSObject/adapter'

import { fetchFolders, fetchMap } from '../actions/draftActionsTS'

const initialState = {
  name: '',
  avatar: {},
  pageId: null,
  selection: {},
  botFields: [],
  botFieldsSettings: {},
  map: { fetching: false, item: null, byId: {} },
  folders: null,
  isProtected: false,
  isLocked: true,
  isTracked: false,
  template: null,
  flowsLockedStatus: undefined,
}

const writableProps = [
  'name',
  'avatar',
  'isProtected',
  'isLocked',
  'isTracked',
  'flowsLockedStatus',
]

/**
 * Filter selection to remove corrupted items, icon items and items depending on icon items
 * @param {Object} selection Current selection state
 * @param {Object|null} contentMap Adapted content map
 * @return {Object} Filtered selection
 */
function filterSelection(selection, contentMap) {
  const itemsMap = get(contentMap, 'byId')
  return Object.keys(selection)
    .filter(
      (id) =>
        selection[id] &&
        (itemsMap == null ||
          (itemsMap[id] &&
            !itemsMap[id].is_corrupted &&
            !getIsIconItem(itemsMap[id]) &&
            !getHasIconDependency(contentMap, id))),
    )
    .reduce((map, id) => ((map[id] = true), map), {})
}

/**
 * Update bot fields based on dependent fields in the content map
 * @param {Object} state Current state
 * @param {Object} selection Current selection
 * @return {Array|null} Updated bot fields or null if no map is available
 */
function updateDependentBotFields(state, selection) {
  const contentMap = get(state, 'map.item')
  if (!contentMap) {
    return null
  }

  const dependentFields = getDependentBotFields(contentMap, selection)
  return sortBy(dependentFields, (currentField) =>
    state.botFields.findIndex((stateField) => stateField.field_id === currentField.field_id),
  ).map((field) => ({ ...field }))
}

export default function templateDraft(state = initialState, action) {
  switch (action.type) {
    case atypes.TEMPLATE_DRAFT_INIT:
      {
        const {
          pageId,
          selection,
          name,
          settingsFields,
          isProtected,
          isLocked,
          isTracked,
          flowsLockedStatus,
        } = action
        const fields = settingsFields || []
        // Filling botFields to keep order, see botFields sorting below
        const botFields = fields.map((field) => ({ field_id: field.field_id }))
        const botFieldsSettings = fields.reduce((map, field) => {
          map[field.field_id] = {
            ...field,
            override: Boolean(field.caption || field.description),
          }
          return map
        }, {})

        state = {
          ...initialState,
          pageId,
          selection: selection || {},
          name: name || '',
          botFields,
          botFieldsSettings,
          isProtected,
          isLocked,
          isTracked,
          flowsLockedStatus,
          map: { ...state.map, fetching: true },
        }
      }
      break
    case atypes.TEMPLATE_DRAFT_UPDATE:
      {
        const { property, value } = action
        if (!writableProps.includes(property)) {
          throw new TemplateDraftInvalidPropertyError(property)
        }

        state = dot.set(state, property, value)
      }
      break
    case atypes.TEMPLATE_DRAFT_SELECTION_SET:
      {
        const selection = { ...state.selection }
        const { id } = action
        if (Array.isArray(id)) {
          id.forEach((id) => (selection[id] = action.isSelected))
        } else {
          selection[action.id] = action.isSelected
        }

        // Filter selection
        const filteredSelection = filterSelection(selection, get(state, 'map.item'))
        state = { ...state, selection: filteredSelection }

        // Update bot fields based on the new selection
        const updatedBotFields = updateDependentBotFields(state, filteredSelection)
        if (updatedBotFields) {
          state = { ...state, botFields: updatedBotFields }
        }
      }
      break
    case atypes.TEMPLATE_DRAFT_SELECTION_ALL:
      {
        let items = get(state, 'map.item.items', [])
        const templateInstallations = get(state, 'map.item.models.template_installations', [])

        if (action.modelId) {
          items = items.filter((item) => item.model_id === action.modelId)
        }

        const itemsWithoutProtected = items.filter((item) => {
          return (
            !getIsProtected(templateInstallations, item.template_installation_id) ||
            getIsAvailable(templateInstallations, item.template_installation_id, action.userId)
          )
        })
        const selection = itemsWithoutProtected.reduce(
          (map, item) => {
            map[item.id] = true
            return map
          },
          {
            ...state.selection,
          },
        )

        // Filter selection
        const filteredSelection = filterSelection(selection, get(state, 'map.item'))
        state = { ...state, selection: filteredSelection }

        // Update bot fields based on the new selection
        const updatedBotFields = updateDependentBotFields(state, filteredSelection)
        if (updatedBotFields) {
          state = { ...state, botFields: updatedBotFields }
        }
      }
      break
    case atypes.TEMPLATE_DRAFT_SELECTION_CLEAR:
      {
        let selection = {}
        if (action.modelId) {
          const itemsMap = get(state, 'map.item.byId', {})
          selection = Object.keys(state.selection)
            .filter((id) => itemsMap[id].model_id !== action.modelId)
            .reduce((map, id) => {
              map[id] = true
              return map
            }, {})
        }

        // Filter selection
        const filteredSelection = filterSelection(selection, get(state, 'map.item'))
        state = { ...state, selection: filteredSelection }

        // Update bot fields based on the new selection
        const updatedBotFields = updateDependentBotFields(state, filteredSelection)
        if (updatedBotFields) {
          state = { ...state, botFields: updatedBotFields }
        }
      }
      break
    case atypes.TEMPLATE_DRAFT_BOT_FIELD_UPDATE:
      {
        const { id, data } = action
        state = dot.merge(state, `botFieldsSettings.${id}`, data)
      }
      break
    case atypes.TEMPLATE_DRAFT_BOT_FIELDS_SORT:
      {
        const { sourceId, targetId } = action
        const botFields = [...state.botFields]
        const sourceIndex = botFields.findIndex((field) => field.field_id === sourceId)
        const targetIndex = botFields.findIndex((field) => field.field_id === targetId)
        const source = botFields[sourceIndex]
        botFields[sourceIndex] = botFields[targetIndex]
        botFields[targetIndex] = source
        state = { ...state, botFields }
      }
      break

    case updateTemplate.pending.type:
    case createTemplate.pending.type: {
      return { ...state, fetching: true }
    }
    case setShareType.fulfilled.type:
    case updateTemplate.fulfilled.type:
    case createTemplate.fulfilled.type: {
      const template = action.payload
      return { ...state, fetching: false, template }
    }
    case fetchSingleuseLink.pending.type:
      state = {
        ...state,
        template: { ...state.template, isGeneratingLink: true },
      }
      break
    case fetchSingleuseLink.fulfilled.type:
      state = {
        ...state,
        template: { ...state.template, ...action.payload, isGeneratingLink: false },
      }
      break
    case updateTemplate.rejected.type:
    case createTemplate.rejected.type: {
      return { ...state, fetching: false }
    }
    case fetchFolders.fulfilled.type: {
      const folders = action.payload
      const items = folders.map(FSOAdapter)
      state = { ...state, folders: items }
      break
    }
    case fetchMap.pending.type: {
      state = { ...state, map: { ...state.map, fetching: true } }
      break
    }
    case fetchMap.fulfilled.type: {
      const newMap = action.payload

      // Filter selection
      const filteredSelection = filterSelection(state.selection, newMap)

      // First update the state with filtered selection and map
      const intermediateState = {
        ...state,
        selection: filteredSelection,
        map: { item: newMap, fetching: false },
      }

      // Update bot fields using the updated state
      const updatedBotFields = updateDependentBotFields(intermediateState, filteredSelection)

      state = {
        ...intermediateState,
        botFields: updatedBotFields || state.botFields,
      }
      break
    }
  }

  return state
}
